import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnInit,
} from '@angular/core';
import { filtersetFiltersFromFilterDTO } from 'src/app/common/dtos/translators/report-filters.translator';
import { ReportFiltersetDTO } from 'src/app/private/shared/dtos/report-filterset.dto';
import { ReportFiltersetService } from 'src/app/private/shared/services/report/report-filterset.service';
import { ReportPageFiltersService } from 'src/app/private/shared/services/report/report-page-filters.service';

@Component({
  selector: 'app-saved-filters-dropdown',
  templateUrl: './saved-filters-dropdown.component.html',
  styleUrls: ['./saved-filters-dropdown.component.scss'],
  standalone: false,
})
export class SavedFiltersDropdownComponent implements OnInit {
  @Input() reportSlug: string;

  @Input() canFilterByDistrict: boolean;

  isOpen = false;

  renamingRowId: number | null = null;

  renameValue = '';

  renameError: string | null = null;

  deletingRowId: number | null = null;

  showSavePanel = false;

  newFiltersetName = '';

  nameValidationError: string | null = null;

  isSaving = false;

  isUpdating = false;

  get filtersets(): ReportFiltersetDTO[] {
    return this.filterService.savedFiltersetsMap[this.reportSlug] ?? [];
  }

  get activeFilterset(): ReportFiltersetDTO | null {
    const id = this.filterService.activeFiltersetIdMap[this.reportSlug];
    if (!id) return null;
    return this.filtersets.find((f) => f.id === id) ?? null;
  }

  get isDrifted(): boolean {
    return !!this.filterService.filtersetsDriftMap[this.reportSlug];
  }

  get triggerLabel(): string {
    if (this.filtersets.length === 0) {
      return 'Save filters';
    }
    const active = this.activeFilterset;
    if (!active) return 'Select saved filters';
    return active.title ?? 'Select saved filters';
  }

  get triggerStyle(): 'dashed' | 'solid' {
    return this.filtersets.length === 0 ? 'dashed' : 'solid';
  }

  get atCap(): boolean {
    return this.filtersets.length >= 50;
  }

  constructor(
    private filterService: ReportPageFiltersService,
    private filtersetService: ReportFiltersetService,
    private elementRef: ElementRef
  ) {}

  ngOnInit(): void {
    this.filterService.loadSavedFiltersets(this.reportSlug);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node;
    // If the clicked element was removed from the DOM before the event bubbled
    // (e.g. an *ngIf toggled it away), treat it as an inside click and ignore.
    if (!document.body.contains(target)) {
      return;
    }
    if (!this.elementRef.nativeElement.contains(target)) {
      this.close();
    }
  }

  open(): void {
    this.isOpen = true;
  }

  close(): void {
    this.isOpen = false;
    this.showSavePanel = false;
    this.cancelRename();
    this.cancelDelete();
  }

  toggle(): void {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  applyFilterset(fs: ReportFiltersetDTO): void {
    this.filterService.applyFilterset(fs, this.reportSlug);
    this.close();
  }

  startRename(id: number, currentName: string): void {
    this.renamingRowId = id;
    this.renameValue = currentName;
    this.renameError = null;
  }

  cancelRename(): void {
    this.renamingRowId = null;
    this.renameValue = '';
    this.renameError = null;
  }

  confirmRename(id: number): void {
    const name = this.renameValue.trim();
    if (!name) {
      this.renameError = 'Name is required.';
      return;
    }
    if (name.length > 60) {
      this.renameError = 'Name must be 60 characters or fewer.';
      return;
    }

    this.filtersetService
      .updateFilterset(id, { title: name, filters: [] })
      .subscribe({
        next: () => {
          const list = this.filterService.savedFiltersetsMap[this.reportSlug];
          if (list) {
            const idx = list.findIndex((f) => f.id === id);
            if (idx !== -1) {
              list[idx] = { ...list[idx], title: name };
            }
          }
          this.cancelRename();
        },
        error: (err) => {
          this.renameError =
            err?.error?.title?.[0] ?? 'Could not rename filter set.';
        },
      });
  }

  startDelete(id: number): void {
    this.deletingRowId = id;
  }

  cancelDelete(): void {
    this.deletingRowId = null;
  }

  confirmDelete(id: number): void {
    this.filtersetService.deleteFilterset(id).subscribe({
      next: () => {
        const list = this.filterService.savedFiltersetsMap[this.reportSlug];
        if (list) {
          this.filterService.savedFiltersetsMap[this.reportSlug] = list.filter(
            (f) => f.id !== id
          );
        }
        if (this.filterService.activeFiltersetIdMap[this.reportSlug] === id) {
          this.filterService.activeFiltersetIdMap[this.reportSlug] = null;
          this.filterService.filtersetsDriftMap[this.reportSlug] = false;
        }
        this.cancelDelete();
      },
    });
  }

  updateActiveFilterset(): void {
    const activeId = this.filterService.activeFiltersetIdMap[this.reportSlug];
    if (!activeId) return;
    this.isUpdating = true;
    this.filtersetService
      .updateFilterset(activeId, {
        filters: filtersetFiltersFromFilterDTO(
          this.filterService.currentFilters
        ),
      })
      .subscribe({
        next: () => {
          this.filterService.filtersetsDriftMap[this.reportSlug] = false;
          this.isUpdating = false;
          this.close();
        },
        error: () => {
          this.isUpdating = false;
        },
      });
  }

  openSavePanel(): void {
    this.showSavePanel = true;
    this.newFiltersetName = '';
    this.nameValidationError = null;
  }

  cancelSavePanel(): void {
    this.showSavePanel = false;
    this.newFiltersetName = '';
    this.nameValidationError = null;
  }

  saveCurrentFilters(): void {
    const name = this.newFiltersetName.trim();
    if (!name) {
      this.nameValidationError = 'Name is required.';
      return;
    }
    if (name.length > 60) {
      this.nameValidationError = 'Name must be 60 characters or fewer.';
      return;
    }

    this.isSaving = true;
    this.filterService.saveNamedFilterset(name, this.reportSlug);
    this.isSaving = false;
    this.cancelSavePanel();
  }

  // eslint-disable-next-line class-methods-use-this
  trackByFiltersetId(_: number, fs: ReportFiltersetDTO): number {
    return fs.id;
  }
}
